/**
 * Error Handler Utility
 * Centralized error handling following constitution standards
 */

// Error types for consistent error handling
export const ERROR_TYPES = {
  NETWORK: 'NETWORK_ERROR',
  VALIDATION: 'VALIDATION_ERROR',
  AUTHENTICATION: 'AUTHENTICATION_ERROR',
  AUTHORIZATION: 'AUTHORIZATION_ERROR',
  SUBSCRIPTION: 'SUBSCRIPTION_ERROR',
  SERVER: 'SERVER_ERROR',
  UNKNOWN: 'UNKNOWN_ERROR'
};

// HTTP status code mappings
export const HTTP_STATUS_MAPPING = {
  400: { type: ERROR_TYPES.VALIDATION, message: 'Невалидна заявка' },
  401: { type: ERROR_TYPES.AUTHENTICATION, message: 'Неоторизиран достъп' },
  402: { type: ERROR_TYPES.SUBSCRIPTION, message: 'Необходим е активен абонамент' },
  403: { type: ERROR_TYPES.AUTHORIZATION, message: 'Достъпът е отказан' },
  404: { type: ERROR_TYPES.VALIDATION, message: 'Ресурсът не е намерен' },
  408: { type: ERROR_TYPES.NETWORK, message: 'Времето за заявката изтече' },
  429: { type: ERROR_TYPES.VALIDATION, message: 'Твърде много заявки' },
  500: { type: ERROR_TYPES.SERVER, message: 'Вътрешна сървърна грешка' },
  502: { type: ERROR_TYPES.SERVER, message: 'Лош шлюз' },
  503: { type: ERROR_TYPES.SERVER, message: 'Службата е недостъпна' },
  504: { type: ERROR_TYPES.NETWORK, message: 'Времето за шлюза изтече' }
};

/**
 * Create standardized error object
 * @param {string} type - Error type from ERROR_TYPES
 * @param {string} message - User-friendly error message
 * @param {string} details - Technical error details
 * @param {Object} originalError - Original error object
 * @returns {Object} Standardized error object
 */
export const createError = function(type, message, details, originalError) {
  details = details || null;
  originalError = originalError || null;

  const error = {
    id: `error-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
    type: type,
    message: message,
    details: details,
    timestamp: new Date().toISOString(),
    userFriendly: true
  };

  if (originalError) {
    error.originalError = originalError;
    error.stack = originalError.stack;
  }

  return error;
};

/**
 * Handle HTTP errors and convert to standardized format
 * @param {Object} error - Axios error object
 * @returns {Object} Standardized error object
 */
export const handleHttpError = function(error) {
  if (!error.response) {
    // Network error
    return createError(
      ERROR_TYPES.NETWORK,
      'Грешка в мрежата. Моля, проверете връзката си.',
      error.message,
      error
    );
  }

  const { status, data } = error.response;
  const statusInfo = HTTP_STATUS_MAPPING[status] || HTTP_STATUS_MAPPING[500];

  // Get message from response data or use default
  const message = (data && data.message) || (data && data.error) || statusInfo.message;

  return createError(
    statusInfo.type,
    message,
    {
      status,
      statusText: error.response.statusText,
      url: error.config && error.config.url,
      method: error.config && error.config.method
    },
    error
  );
};

/**
 * Handle validation errors
 * @param {Object} validationErrors - Validation error object
 * @returns {Object} Standardized error object
 */
export const handleValidationError = function(validationErrors) {
  const details = Object.keys(validationErrors).map(function(field) {
    const errors = validationErrors[field];
    return {
      field: field,
      errors: Array.isArray(errors) ? errors : [errors]
    };
  });

  return createError(
    ERROR_TYPES.VALIDATION,
    'Невалидни данни. Моля, проверете въведената информация.',
    details
  );
};

/**
 * Handle authentication errors
 * @param {string} message - Authentication error message
 * @param {Object} originalError - Original error object
 * @returns {Object} Standardized error object
 */
export const handleAuthError = function(message, originalError) {
  originalError = originalError || null;
  return createError(
    ERROR_TYPES.AUTHENTICATION,
    message || 'Грешка в аутентификацията. Моля, влезте отново.',
    null,
    originalError
  );
};

/**
 * Handle subscription errors
 * @param {string} message - Subscription error message
 * @param {Object} originalError - Original error object
 * @returns {Object} Standardized error object
 */
export const handleSubscriptionError = function(message, originalError) {
  originalError = originalError || null;
  return createError(
    ERROR_TYPES.SUBSCRIPTION,
    message || 'Необходим е активен абонамент за достъп до тази функция.',
    null,
    originalError
  );
};

/**
 * Log error for debugging (development) or monitoring (production)
 * @param {Object} error - Standardized error object
 * @param {string} context - Error context (component name, action, etc.)
 */
export const logError = function(error, context) {
  context = context || 'Unknown';
  const logData = {
    timestamp: error.timestamp,
    errorId: error.id,
    type: error.type,
    message: error.message,
    context: context,
    details: error.details
  };

  if (process.env.NODE_ENV === 'development') {
    console.group(`🚨 Error in ${context}`);
    console.error('Error Details:', logData);
    if (error.originalError) {
      console.error('Original Error:', error.originalError);
    }
    console.groupEnd();
  } else {
    // In production, send to error logging service
    // TODO: Implement error logging service integration
    console.error('Production Error:', logData);
  }
};

/**
 * Check if error is recoverable
 * @param {Object} error - Standardized error object
 * @returns {boolean} True if error is recoverable
 */
export const isRecoverableError = function(error) {
  const recoverableTypes = [
    ERROR_TYPES.NETWORK,
    ERROR_TYPES.VALIDATION,
    ERROR_TYPES.SUBSCRIPTION
  ];

  return recoverableTypes.includes(error.type);
};

/**
 * Get user-friendly error message
 * @param {Object} error - Standardized error object
 * @param {string} language - Language code (bg/en)
 * @returns {string} Localized error message
 */
export const getUserFriendlyMessage = function(error, language) {
  language = language || 'bg';
  // Default Bulgarian messages (following constitution)
  const messages = {};
  messages.bg = {};
  messages.bg[ERROR_TYPES.NETWORK] = 'Грешка в мрежата. Моля, проверете връзката си.';
  messages.bg[ERROR_TYPES.VALIDATION] = 'Невалидни данни. Моля, проверете въведената информация.';
  messages.bg[ERROR_TYPES.AUTHENTICATION] = 'Грешка в аутентификацията. Моля, влезте отново.';
  messages.bg[ERROR_TYPES.AUTHORIZATION] = 'Нямате права за достъп до този ресурс.';
  messages.bg[ERROR_TYPES.SUBSCRIPTION] = 'Необходим е активен абонамент за достъп до тази функция.';
  messages.bg[ERROR_TYPES.SERVER] = 'Сървърна грешка. Моля, опитайте по-късно.';
  messages.bg[ERROR_TYPES.UNKNOWN] = 'Възникна неочаквана грешка. Моля, опитайте отново.';

  messages.en = {};
  messages.en[ERROR_TYPES.NETWORK] = 'Network error. Please check your connection.';
  messages.en[ERROR_TYPES.VALIDATION] = 'Invalid data. Please check the information you entered.';
  messages.en[ERROR_TYPES.AUTHENTICATION] = 'Authentication error. Please log in again.';
  messages.en[ERROR_TYPES.AUTHORIZATION] = 'You do not have permission to access this resource.';
  messages.en[ERROR_TYPES.SUBSCRIPTION] = 'Active subscription required to access this feature.';
  messages.en[ERROR_TYPES.SERVER] = 'Server error. Please try again later.';
  messages.en[ERROR_TYPES.UNKNOWN] = 'An unexpected error occurred. Please try again.';

  return (messages[language] && messages[language][error.type]) || messages.bg[ERROR_TYPES.UNKNOWN];
};

/**
 * Get suggested actions for error recovery
 * @param {Object} error - Standardized error object
 * @param {string} language - Language code (bg/en)
 * @returns {Array} Array of suggested actions
 */
export const getErrorActions = function(error, language) {
  language = language || 'bg';
  const actions = {};
  actions.bg = {};
  actions.bg[ERROR_TYPES.NETWORK] = ['refresh', 'checkConnection'];
  actions.bg[ERROR_TYPES.VALIDATION] = ['checkInput', 'tryAgain'];
  actions.bg[ERROR_TYPES.AUTHENTICATION] = ['login', 'contactSupport'];
  actions.bg[ERROR_TYPES.AUTHORIZATION] = ['checkPermissions', 'contactSupport'];
  actions.bg[ERROR_TYPES.SUBSCRIPTION] = ['activateSubscription', 'contactSupport'];
  actions.bg[ERROR_TYPES.SERVER] = ['tryAgain', 'contactSupport'];
  actions.bg[ERROR_TYPES.UNKNOWN] = ['tryAgain', 'contactSupport'];

  actions.en = {};
  actions.en[ERROR_TYPES.NETWORK] = ['refresh', 'checkConnection'];
  actions.en[ERROR_TYPES.VALIDATION] = ['checkInput', 'tryAgain'];
  actions.en[ERROR_TYPES.AUTHENTICATION] = ['login', 'contactSupport'];
  actions.en[ERROR_TYPES.AUTHORIZATION] = ['checkPermissions', 'contactSupport'];
  actions.en[ERROR_TYPES.SERVER] = ['tryAgain', 'contactSupport'];
  actions.en[ERROR_TYPES.UNKNOWN] = ['tryAgain', 'contactSupport'];

  return (actions[language] && actions[language][error.type]) || actions.bg[ERROR_TYPES.UNKNOWN];
};

/**
 * Format error for display
 * @param {Object} error - Standardized error object
 * @param {string} language - Language code (bg/en)
 * @returns {Object} Formatted error for UI display
 */
export const formatErrorForDisplay = function(error, language) {
  language = language || 'bg';
  return {
    id: error.id,
    type: error.type,
    title: getUserFriendlyMessage(error, language),
    message: error.message,
    details: error.details,
    isRecoverable: isRecoverableError(error),
    timestamp: error.timestamp,
    actions: getErrorActions(error, language)
  };
};


